import * as React from 'react';
import Link from 'components/Link';
import { useRouter } from 'next/router';
import { Dialog, Transition } from '@headlessui/react';
import { CheckIcon, XIcon, ArrowUpIcon } from '@heroicons/react/outline';
import { SiDiscord } from 'react-icons/si';
import { FaApple, FaGooglePlay } from 'react-icons/fa';
import { IoExitOutline } from 'react-icons/io5';
import { BsArrowRight } from 'react-icons/bs';
import { getIsNativePlatform } from 'utils/client/mobile';
import useScript from 'hooks/useScript';
import {
  useUpdateUserInsightsSettingMutation,
  useUpdateUsernameMutation,
} from 'types/generated/client';
import { auth } from 'services/client/firebase';
import api from 'services/client/api';
import Sentry from 'utils/shared/sentry';
import InformationPage from 'layouts/InformationPage';
import MiniInput from 'components/MiniInput';
import Card from 'components/Card';
import {
  ELECTRONIC_AGREEMENT_PAGE,
  TERMS_OF_SERVICE_PAGE,
  LOGOUT_PAGE,
  SIGNUP_PAGE,
  AFFILIATE_DASHBOARD_PAGE,
  DISCORD_AUTH_API,
  COURSES_INDEX_PAGE,
} from 'constants/pages';
import { RequestStatus } from 'constants/requests';
import { TEMP_FIREBASE_COOKIE } from 'constants/auth';
import PulseLoader from 'components/Loading/Pulse';
import PricingCards from 'components/PricingCards';
import AddTradingviewUsername from 'components/TradingView/AddTradingviewUsername';
import {
  ALERTS_SHORT_NAME,
  DASHBOARD_SHORT_NAME,
  productTerms,
  SubscriptionType,
  DisplayedSubscriptionNames,
  SaleTerm,
} from 'constants/payments';
import { getAffiliateUrl } from 'utils/shared/affiliate';
import { ProfitwellCancelPayload } from 'constants/subscriptions';
import { createAffiliateAccount } from 'services/client/pipedream';
import classNames from 'styles/utils/classNames';
import {
  MORNING_EMAIL_LIST_ID,
  AFTERNOON_EMAIL_LIST_ID,
  EVENING_EMAIL_LIST_ID,
  WEEKEND_EMAIL_LIST_ID,
} from 'constants/emails';
import { Props, SettingsApiResponse } from './props';
import TimezoneSettings from './TimezoneDetails';
import { PostRequestPayload } from 'constants/payloads/email-list/status';

import {
  Label,
  Input,
  ViewLink,
  LinkDescription,
  StatusActive,
  StatusEnding,
  LogoutButton,
  UpgradeBox,
  UpgradeButtonLink,
  UpgradeButton,
  SessionLink,
  DiscordButton,
  SliderWrapper,
  SliderActive,
  SliderButton,
  UsernameButton,
  InlineCheckbox,
  CheckboxInlineLabel,
  InlineCheckboxWrapperLabel,
} from './styles';

const IS_USING_BREVO_LISTS = true;
const PAGE_TITLE = 'Settings';
const DISCORD_SERVER_URL = 'https://discord.gg/6A3M7gXVPw';
const SCRIPT_SRC = 'https://cdn.prosperstack.com/flow/1.0.0/flow.min.js';
const USE_PROFITWELL_CANCEL_FLOW = true;
const NEW_CANCEL_QUERY = 'isnewcancel';
const resubscribePrices = {
  discount: 0.3,
  dashboard: {
    monthly: 75,
    quarterly: 195,
    annual: 660,
  },
  alerts: {
    monthly: 39,
    quarterly: 102,
    annual: 348,
  },
};
const upgradePrices = {
  discount: 0.2,
  dashboard: {
    annual: 660,
    lifetime: 1500,
  },
  alerts: {
    annual: 348,
    lifetime: 750,
  },
};

const PageWrapper: React.FC<React.PropsWithChildren<unknown>> = ({ children }) => {
  return <InformationPage title={PAGE_TITLE}>{children}</InformationPage>;
};

const ResubscribeCard: React.FC<
  React.PropsWithChildren<{
    term: string;
    price: number;
    discount: number;
    duration: string;
    subscriptionType: typeof ALERTS_SHORT_NAME | typeof DASHBOARD_SHORT_NAME;
    setResubscribeRequestStatus: (status: RequestStatus) => void;
  }>
> = ({ term, price, discount, duration, subscriptionType, setResubscribeRequestStatus }) => {
  return (
    <div className="text-center">
      <div className="text-lg font-semibold text-gray-300">{term}</div>
      <div className="mt-2">
        <span className="text-sm text-red-400 line-through">${price}</span>
        <span className="ml-2 font-semibold">${(price * (1 - discount)).toFixed(2)}</span>
      </div>
      <button
        onClick={async () => {
          try {
            setResubscribeRequestStatus(RequestStatus.InProgress);
            await api.post('payments/stripe/subscriptions/resubscribe', {
              payload: {
                duration,
                subscriptionType,
              },
            });
            setResubscribeRequestStatus(RequestStatus.Success);
          } catch (_error) {
            setResubscribeRequestStatus(RequestStatus.Error);
          }
        }}
        className="mt-4 inline-flex w-full justify-center rounded-md border border-transparent bg-button-color px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-button-color focus:ring-offset-2 sm:col-start-2 sm:text-sm"
      >
        Subscribe Now
      </button>
    </div>
  );
};

export default function SettingsPage({ viewer, settings }: React.PropsWithChildren<Props>) {
  useScript({
    src: SCRIPT_SRC,
  });

  const router = useRouter();
  const fullName = `${settings?.firstName || ''} ${settings?.lastName || ''}`;
  const affiliateId = settings?.usernameCaseSensitive || settings?.username;
  const hasSubscriptions = !!settings?.subscriptions && settings.subscriptions.length > 0;
  const isDashboardLifetime = settings?.isDashboardLifetime;
  const isTechnicalAnalysisLifetime = settings?.isTechnicalAnalysisLifetime;
  const [updateUserInsightsSetting] = useUpdateUserInsightsSettingMutation();
  const [updateUsername, { error, loading }] = useUpdateUsernameMutation();
  const [isCreatingSession, setIsCreatingSession] = React.useState(false);
  const [subToCancel, setSubToCancel] = React.useState('');
  const [hasCancelError, setHasCancelError] = React.useState(false);
  const [isUpgradeOpen, setIsUpgradeOpen] = React.useState(false);
  const [isResubscribeOpen, setIsResubscribeOpen] = React.useState(false);
  const [passwordResetSuccess, setPasswordResetSuccess] = React.useState(false);
  const [upgradeRequestStatus, setUpgradeRequestStatus] = React.useState(RequestStatus.Idle);
  const [resubscribeRequestStatus, setResubscribeRequestStatus] = React.useState(
    RequestStatus.Idle,
  );
  const [isDiscordAuth, setIsDiscordAuth] = React.useState(false);
  const [isReceivingInsights, setIsReceivingInsights] = React.useState(
    settings?.emailInsights || false,
  );
  const [isReceivingMorning, setIsReceivingMorning] = React.useState(
    settings?.emailListStatuses.morning || false,
  );
  const [isReceivingAfternoon, setIsReceivingAfternoon] = React.useState(
    settings?.emailListStatuses.afternoon || false,
  );
  const [isReceivingEvening, setIsReceivingEvening] = React.useState(
    settings?.emailListStatuses.evening || false,
  );
  const [isReceivingWeekend, setIsReceivingWeekend] = React.useState(
    settings?.emailListStatuses.weekend || false,
  );
  const [username, setUsername] = React.useState('');
  const [isAffiliateSubmitting, setIsAffiliateSubmitting] = React.useState(false);

  React.useEffect(() => {
    if (settings?.emailListStatuses.morning) {
      setIsReceivingMorning(true);
    }
  }, [settings?.emailListStatuses.morning]);
  React.useEffect(() => {
    if (settings?.emailListStatuses.afternoon) {
      setIsReceivingAfternoon(true);
    }
  }, [settings?.emailListStatuses.afternoon]);
  React.useEffect(() => {
    if (settings?.emailListStatuses.evening) {
      setIsReceivingEvening(true);
    }
  }, [settings?.emailListStatuses.evening]);
  React.useEffect(() => {
    if (settings?.emailListStatuses.weekend) {
      setIsReceivingWeekend(true);
    }
  }, [settings?.emailListStatuses.weekend]);

  const stripeSubscriptionDetails = React.useMemo(() => {
    const details = {
      mostRecentPriceId: '',
      mostRecentSubscriptionId: '',
      mostRecentDuration: '',
      mostRecentDurationCount: 0,
      mostRecentInternalSubscription: null as null | SettingsApiResponse['subscriptions'][0],
      firstActivePriceId: '',
      firstActiveSubscriptionId: '',
      firstActiveDuration: '',
      firstActiveDurationCount: 0,
      firstActiveInternalSubscription: null as null | SettingsApiResponse['subscriptions'][0],
      hasAnyActive: false,
      isActiveAlsoMostRecent: false,
      isFirstActiveTechnicalAnalysis: false,
    };

    if (!settings?.stripeSubscriptions || settings.stripeSubscriptions.length === 0) {
      return details;
    }

    details.mostRecentPriceId = settings.stripeSubscriptions[0].priceId;
    details.mostRecentSubscriptionId = settings.stripeSubscriptions[0].subscriptionId;
    details.mostRecentDuration = settings.stripeSubscriptions[0].duration;
    details.mostRecentDurationCount = settings.stripeSubscriptions[0].durationCount;

    const activeSubscription = settings?.stripeSubscriptions?.find((subscription) => {
      return subscription.status === 'active' && !subscription.cancel_at_period_end;
    });

    details.hasAnyActive = !!activeSubscription;
    details.firstActivePriceId = activeSubscription?.priceId || '';
    details.firstActiveSubscriptionId = activeSubscription?.subscriptionId || '';
    details.firstActiveDuration = activeSubscription?.duration || '';
    details.firstActiveDurationCount = activeSubscription?.durationCount || 0;
    details.isActiveAlsoMostRecent = details.mostRecentPriceId === details.firstActivePriceId;

    const mostRecentInternalSubscription = settings?.subscriptions?.find((subscription) => {
      return subscription.externalSubscriptionId === details.mostRecentSubscriptionId;
    });
    const firstActiveInternalSubscription = settings?.subscriptions?.find((subscription) => {
      return subscription.externalSubscriptionId === details.firstActiveSubscriptionId;
    });

    details.mostRecentInternalSubscription = mostRecentInternalSubscription || null;
    details.firstActiveInternalSubscription = firstActiveInternalSubscription || null;
    details.isFirstActiveTechnicalAnalysis =
      firstActiveInternalSubscription?.type === SubscriptionType.SmartMoneyAlerts;

    return details;
  }, [settings]);

  React.useEffect(() => {
    setIsReceivingInsights(settings?.emailInsights || false);
  }, [settings]);

  React.useEffect(() => {
    if (router.isReady && router.query.upgrade === 'true') {
      setIsUpgradeOpen(true);
    }
  }, [router]);

  if (!settings || !viewer.viewer) {
    return (
      <PageWrapper>
        <div className="flex items-center justify-center pt-12">
          <PulseLoader />
        </div>
      </PageWrapper>
    );
  }

  const handleCreatePortalSession = () => {
    if (!isCreatingSession) {
      setIsCreatingSession(true);

      auth.currentUser
        ?.getIdToken(true)
        .then(function (idToken) {
          if (!idToken) {
            window.location.assign(SIGNUP_PAGE);
            return;
          }

          return api.post('payments/stripe/portal', {
            payload: {
              token: idToken,
            },
          });
        })
        .then(function (session) {
          if (session && session.statusCode && session.statusCode > 201 && session.message) {
            throw new Error(session.message);
            return;
          }

          if (!session || !session.id) {
            throw new Error('Could not create subscription. Refresh the page and try again.');
            return;
          }

          return (window.location.href = session.url);
        })
        .catch(function (error) {
          Sentry.captureException(error);
          if (error.message !== 'Failed to fetch') {
            alert(error.message);
          }
          setIsCreatingSession(false);
        });
    }
  };

  const handleEnterProsperStackCancelFlow = (subscriptionId: string) => {
    if (!isCreatingSession) {
      // @ts-ignore ProsperStack wille exist after script loads
      if (typeof window !== 'undefined' && window?.ProsperStack?.flow) {
        setIsCreatingSession(true);

        auth.currentUser
          ?.getIdToken(true)
          .then(function (idToken) {
            if (!idToken) {
              window.location.assign(SIGNUP_PAGE);
              return;
            }

            return api.post('payments/cancelations/sign', {
              payload: {
                token: idToken,
                flowId: process.env.PROSPERSTACK_FLOW_ID,
                subscriptionId,
              },
            });
          })
          .then(({ payload, signature }) => {
            // @ts-ignore ProsperStack wille exist after script loads
            return window.ProsperStack.flow({
              payload,
              signature,
            });
          })
          .then((resp) => {
            console.log({ resp });
            setIsCreatingSession(false);
            if (resp.status === 'canceled' || resp.status === 'saved') {
              window.location.reload();
            }
          })
          .catch((error) => {
            Sentry.captureException(error);
            if (error.message !== 'Failed to fetch') {
              alert(error.message);
            }
            setIsCreatingSession(false);
          });
      }
    }
  };

  const handleDeleteSubscription = async (
    subscriptionId: string,
    cancelPayload: ProfitwellCancelPayload,
  ) => {
    try {
      const idToken = await auth.currentUser?.getIdToken(true);
      if (!idToken) {
        window.location.assign(SIGNUP_PAGE);
        return;
      }
      await api.delete(`payments/stripe/subscriptions/${subscriptionId}`, {
        payload: { token: idToken, subscriptionId, cancelPayload },
      });
      window.location.reload();
    } catch (error: any) {
      Sentry.captureException(error);
    }
  };

  return (
    <>
      <PageWrapper>
        <div className="mb-20 mt-16 px-4">
          <div className="mx-auto max-w-4xl">
            {settings.affiliateEndedAt ? null : affiliateId ? (
              <UpgradeBox className="mb-6 flex flex-col items-start justify-start rounded p-4 pl-6 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <div className="mb-1 text-xs text-blue-300">Affiliate Link</div>
                  <div>{getAffiliateUrl(affiliateId)}</div>
                </div>
                <Link
                  href={AFFILIATE_DASHBOARD_PAGE}
                  className="mt-4 flex-shrink-0 font-semibold sm:mt-0"
                >
                  <UpgradeButtonLink className="p-4">
                    View Affiliate Page{' '}
                    <span className="ml-2 text-lg">
                      <BsArrowRight />
                    </span>
                  </UpgradeButtonLink>
                </Link>
              </UpgradeBox>
            ) : (
              <div>
                {!!error && (
                  <div className="mb-1 text-sm text-red-400">
                    There was an issue creating your username. Try a different username or return
                    later.
                  </div>
                )}
                <UpgradeBox className="mb-6 flex flex-col items-start justify-start rounded p-4 pl-6 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <div className="mb-1 text-xs text-blue-300">Spread the word and get paid</div>
                    <div className="pr-0 text-sm sm:pr-8">
                      Become an affiliate by creating a username and earn recurring income by
                      telling your friends about InsiderFinance
                    </div>
                  </div>
                  <form
                    className="mt-4 flex-shrink-0 sm:mt-0"
                    style={{ minWidth: '280px' }}
                    onSubmit={async (e) => {
                      e.preventDefault();

                      if (
                        !username ||
                        !username.trim() ||
                        loading ||
                        isAffiliateSubmitting ||
                        !viewer?.viewer?.uid
                      ) {
                        return;
                      }

                      try {
                        setIsAffiliateSubmitting(true);
                        await updateUsername({
                          variables: {
                            id: viewer.viewer.uid,
                            username: username.toLowerCase(),
                            usernameCaseSensitive: username,
                          },
                        });

                        await createAffiliateAccount({
                          id: viewer.viewer.uid,
                          email: viewer.viewer.email,
                          username: username.toLowerCase(),
                        }).catch((error) => {
                          Sentry.captureException(error);
                        });

                        router.push(AFFILIATE_DASHBOARD_PAGE);
                      } catch (error) {
                        // NOTE: Error is in the mutation response
                        Sentry.captureException(error);
                      } finally {
                        setIsAffiliateSubmitting(false);
                      }
                    }}
                  >
                    <MiniInput
                      placeholder="Desired affiliate username..."
                      value={username}
                      onChange={(e) => setUsername(e.target.value.replace(/\W/g, ''))}
                      reset={() => setUsername('')}
                      disabled={isAffiliateSubmitting || loading}
                      button={
                        <UsernameButton
                          className="flex-shrink-0 items-center justify-center px-4 py-2 text-xs"
                          type="submit"
                          disabled={isAffiliateSubmitting || loading}
                        >
                          {isAffiliateSubmitting ? 'Loading...' : 'Submit'}
                        </UsernameButton>
                      }
                    />
                  </form>
                </UpgradeBox>
              </div>
            )}
            <div className="mt-6">
              <AddTradingviewUsername
                viewer={viewer}
                existingTradingviewUsername={settings.tradingviewUsername}
                tradingviewAccessStatus={settings.tradingviewAccessStatus}
              />
            </div>
            <div className="mt-6">
              <div className="flex justify-between">
                <div className="styled-font mb-2 text-xl font-semibold">Account Details</div>
                <div>
                  <Link href={LOGOUT_PAGE}>
                    <LogoutButton>Logout</LogoutButton>
                  </Link>
                </div>
              </div>
              <Card>
                <div className="p-2">
                  <div>
                    <Label>Email</Label>
                    <Input value={viewer.viewer?.email || ''} disabled />
                  </div>
                  <div>
                    <Label>Name</Label>
                    <Input value={fullName} disabled />
                  </div>
                  <a
                    href={TERMS_OF_SERVICE_PAGE}
                    target="_blank"
                    className="mt-4 flex justify-between px-1"
                  >
                    <LinkDescription>Terms of Service</LinkDescription>
                    <ViewLink>View</ViewLink>
                  </a>
                  <Link href={ELECTRONIC_AGREEMENT_PAGE} className="mt-3 flex justify-between px-1">
                    <LinkDescription>Electronic Agreement</LinkDescription>
                    <ViewLink>View</ViewLink>
                  </Link>
                </div>
              </Card>
            </div>
            <div className="mt-6">
              <TimezoneSettings />
            </div>
            <div className="mt-6">
              <div className="styled-font mb-2 text-xl font-semibold">Features</div>
              <Card>
                <div className="mb-4 flex items-center justify-between">
                  <div>
                    <div className="text-lg font-semibold">Discord Community</div>
                    <div className="pr-6 text-xs text-gray-400">
                      Subscribers should click the purple "Join Discord" button first to get access
                      to the premium Discord channels
                    </div>
                    <div>
                      <a
                        href={DISCORD_SERVER_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-1 flex items-center text-xs text-gray-300 underline"
                      >
                        <span className="mr-2">Launch Discord Server</span> <IoExitOutline />
                      </a>
                    </div>
                  </div>
                  <DiscordButton
                    isDisabled={isDiscordAuth}
                    href={DISCORD_AUTH_API}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();

                      if (isDiscordAuth) {
                        return;
                      }

                      setIsDiscordAuth(true);

                      auth.currentUser?.getIdToken(true).then((idToken) => {
                        document.cookie = `${TEMP_FIREBASE_COOKIE}=${idToken}; Max-Age=${
                          10 * 60
                        }; secure; samesite=lax; path=/`;
                        window.location.href = DISCORD_AUTH_API;
                      });
                    }}
                  >
                    <SiDiscord /> <span className="ml-2">Join Discord</span>
                  </DiscordButton>
                </div>
                <div className="mt-8 flex justify-between">
                  <div className="text-lg font-semibold">Email Insights</div>
                  {IS_USING_BREVO_LISTS ? (
                    <div className="flex flex-col gap-2">
                      <div className="flex items-center justify-between gap-2">
                        <div>Morning</div>
                        <SliderWrapper>
                          <SliderActive tabs={2} activeTab={!isReceivingMorning ? 0 : 1} />
                          <SliderButton
                            tabs={2}
                            onClick={() => {
                              setIsReceivingMorning(false);
                              const payload: PostRequestPayload = {
                                addLists: [],
                                removeLists: [
                                  {
                                    listId: MORNING_EMAIL_LIST_ID,
                                    action: 'remove',
                                  },
                                ],
                              };
                              api.post('email-list/status', {
                                payload: payload,
                              });
                            }}
                          >
                            OFF
                          </SliderButton>
                          <SliderButton
                            tabs={2}
                            onClick={() => {
                              setIsReceivingMorning(true);
                              const payload: PostRequestPayload = {
                                addLists: [
                                  {
                                    listId: MORNING_EMAIL_LIST_ID,
                                    action: 'add',
                                  },
                                ],
                                removeLists: [],
                              };
                              api.post('email-list/status', {
                                payload: payload,
                              });
                            }}
                          >
                            ON
                          </SliderButton>
                        </SliderWrapper>
                      </div>
                      <div className="flex items-center justify-between gap-2">
                        <div>Afternoon</div>
                        <SliderWrapper>
                          <SliderActive tabs={2} activeTab={!isReceivingAfternoon ? 0 : 1} />
                          <SliderButton
                            tabs={2}
                            onClick={() => {
                              setIsReceivingAfternoon(false);
                              const payload: PostRequestPayload = {
                                addLists: [],
                                removeLists: [
                                  {
                                    listId: AFTERNOON_EMAIL_LIST_ID,
                                    action: 'remove',
                                  },
                                ],
                              };
                              api.post('email-list/status', {
                                payload: payload,
                              });
                            }}
                          >
                            OFF
                          </SliderButton>
                          <SliderButton
                            tabs={2}
                            onClick={() => {
                              setIsReceivingAfternoon(true);
                              const payload: PostRequestPayload = {
                                addLists: [
                                  {
                                    listId: AFTERNOON_EMAIL_LIST_ID,
                                    action: 'add',
                                  },
                                ],
                                removeLists: [],
                              };
                              api.post('email-list/status', {
                                payload: payload,
                              });
                            }}
                          >
                            ON
                          </SliderButton>
                        </SliderWrapper>
                      </div>
                      <div className="flex items-center justify-between gap-2">
                        <div>Evening</div>
                        <SliderWrapper>
                          <SliderActive tabs={2} activeTab={!isReceivingEvening ? 0 : 1} />
                          <SliderButton
                            tabs={2}
                            onClick={() => {
                              setIsReceivingEvening(false);
                              const payload: PostRequestPayload = {
                                addLists: [],
                                removeLists: [
                                  {
                                    listId: EVENING_EMAIL_LIST_ID,
                                    action: 'remove',
                                  },
                                ],
                              };
                              api.post('email-list/status', {
                                payload: payload,
                              });
                            }}
                          >
                            OFF
                          </SliderButton>
                          <SliderButton
                            tabs={2}
                            onClick={() => {
                              setIsReceivingEvening(true);
                              const payload: PostRequestPayload = {
                                addLists: [
                                  {
                                    listId: EVENING_EMAIL_LIST_ID,
                                    action: 'add',
                                  },
                                ],
                                removeLists: [],
                              };
                              api.post('email-list/status', {
                                payload: payload,
                              });
                              // }
                            }}
                          >
                            ON
                          </SliderButton>
                        </SliderWrapper>
                      </div>
                      <div className="flex items-center justify-between gap-2">
                        <div>Weekend</div>
                        <SliderWrapper>
                          <SliderActive tabs={2} activeTab={!isReceivingWeekend ? 0 : 1} />
                          <SliderButton
                            tabs={2}
                            onClick={() => {
                              setIsReceivingWeekend(false);
                              const payload: PostRequestPayload = {
                                addLists: [],
                                removeLists: [
                                  {
                                    listId: WEEKEND_EMAIL_LIST_ID,
                                    action: 'remove',
                                  },
                                ],
                              };
                              api.post('email-list/status', {
                                payload: payload,
                              });
                            }}
                          >
                            OFF
                          </SliderButton>
                          <SliderButton
                            tabs={2}
                            onClick={() => {
                              setIsReceivingWeekend(true);
                              const payload: PostRequestPayload = {
                                addLists: [
                                  {
                                    listId: WEEKEND_EMAIL_LIST_ID,
                                    action: 'add',
                                  },
                                ],
                                removeLists: [],
                              };
                              api.post('email-list/status', {
                                payload: payload,
                              });
                            }}
                          >
                            ON
                          </SliderButton>
                        </SliderWrapper>
                      </div>
                    </div>
                  ) : (
                    <SliderWrapper>
                      <SliderActive tabs={2} activeTab={!isReceivingInsights ? 0 : 1} />
                      <SliderButton
                        tabs={2}
                        onClick={() => {
                          setIsReceivingInsights(false);
                          if (viewer.viewer?.uid) {
                            updateUserInsightsSetting({
                              variables: {
                                id: viewer.viewer.uid,
                                emailInsights: false,
                              },
                            });
                          }
                        }}
                      >
                        OFF
                      </SliderButton>
                      <SliderButton
                        tabs={2}
                        onClick={() => {
                          setIsReceivingInsights(true);
                          if (viewer.viewer?.uid) {
                            updateUserInsightsSetting({
                              variables: {
                                id: viewer.viewer.uid,
                                emailInsights: true,
                              },
                            });
                          }
                        }}
                      >
                        ON
                      </SliderButton>
                    </SliderWrapper>
                  )}
                </div>
              </Card>
            </div>
            <div className="mt-6">
              <div className="styled-font mb-2 text-xl font-semibold">Mobile Apps</div>
              <Card>
                <div className="">
                  <div className="mb-4">
                    <p className="text-pretty text-sm text-gray-300">
                      Access InsiderFinance on the go with our mobile apps. Be sure to{' '}
                      <button
                        onClick={async () => {
                          try {
                            await auth.sendPasswordResetEmail(viewer.viewer?.email || '');
                            setPasswordResetSuccess(true);
                            setTimeout(() => setPasswordResetSuccess(false), 8000);
                          } catch (error) {
                            Sentry.captureException(error);
                            alert(
                              'There was an error sending the password reset email. Please try again later.',
                            );
                          }
                        }}
                        className="inline underline"
                      >
                        create a password
                      </button>{' '}
                      before logging in to the app.
                    </p>
                    {passwordResetSuccess ? (
                      <div className="mt-4 rounded-md bg-green-900/50 p-3 text-sm text-green-300">
                        Check your email inbox for a link to set your password
                      </div>
                    ) : (
                      <button
                        onClick={async () => {
                          try {
                            await auth.sendPasswordResetEmail(viewer.viewer?.email || '');
                            setPasswordResetSuccess(true);
                            setTimeout(() => setPasswordResetSuccess(false), 8000);
                          } catch (error) {
                            Sentry.captureException(error);
                            alert(
                              'There was an error sending the password reset email. Please try again later.',
                            );
                          }
                        }}
                        className="mt-2 text-sm text-blue-400 hover:text-blue-300"
                      >
                        Create Password
                      </button>
                    )}
                  </div>
                  <div className="flex flex-col gap-3 sm:flex-row sm:gap-4">
                    <a
                      href="https://apps.apple.com/us/app/insiderfinance/id6444453657"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center gap-2 rounded-md border border-gray-600 bg-palette-black-3 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-palette-black-4"
                    >
                      <FaApple className="h-5 w-5" />
                      Download for iOS
                    </a>
                    <a
                      href="https://play.google.com/store/apps/details?id=com.insiderfinance.dashboard&pcampaignid=web_share"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center gap-2 rounded-md border border-gray-600 bg-palette-black-3 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-palette-black-4"
                    >
                      <FaGooglePlay className="h-4 w-4" />
                      Download for Android
                    </a>
                  </div>
                </div>
              </Card>
            </div>
            {hasSubscriptions && !getIsNativePlatform() && (
              <div className="mt-6">
                <div className="flex justify-between">
                  <div>
                    <div className="styled-font mb-2 text-xl font-semibold">Subscriptions</div>
                  </div>
                  <div>
                    <SessionLink
                      isCreatingSession={isCreatingSession}
                      className="text-sm underline"
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        if (!isCreatingSession) {
                          handleCreatePortalSession();
                        }
                      }}
                    >
                      Update Payment Details and Subscription
                    </SessionLink>
                  </div>
                </div>
                {!getIsNativePlatform() && !isDashboardLifetime && (
                  <div className="flex justify-center">
                    <UpgradeBox className="mb-6 flex w-full flex-col items-start justify-between rounded p-4 pl-6 sm:flex-row sm:items-center sm:justify-between">
                      <div className="font-semibold">
                        {stripeSubscriptionDetails.hasAnyActive
                          ? stripeSubscriptionDetails.firstActiveDuration === 'year'
                            ? process.env.IS_MEMBER_SALE === 'true'
                              ? 'Upgrade to save big'
                              : isTechnicalAnalysisLifetime
                              ? 'Upgrade to a dashboard lifetime plan to save big'
                              : 'Upgrade to a lifetime plan to save big'
                            : 'Upgrade to an annual or lifetime plan to save big'
                          : 'Reactivate your subscription to receive our biggest LIFETIME discount'}
                      </div>
                      <UpgradeButton
                        onClick={() => {
                          if (stripeSubscriptionDetails.hasAnyActive) {
                            if (process.env.IS_MEMBER_SALE === 'true') {
                              if (!isCreatingSession) {
                                handleCreatePortalSession();
                              }
                            } else {
                              setIsUpgradeOpen(true);
                            }
                          } else {
                            setIsResubscribeOpen(true);
                          }
                        }}
                        className="mt-4 flex-shrink-0 p-4 font-semibold sm:mt-0"
                      >
                        {stripeSubscriptionDetails.hasAnyActive
                          ? 'View Upgrade Options'
                          : 'Reactivate Now at 30% Off'}
                      </UpgradeButton>
                    </UpgradeBox>
                  </div>
                )}
                <Card>
                  <div className="p-2">
                    {settings.subscriptions.map((subscription, i) => {
                      const endDate = new Date(subscription.currentPeriodEndsAt || '');
                      const humanReadableEndDate = endDate.toLocaleDateString();
                      const type = subscription.type as SubscriptionType;
                      const name = DisplayedSubscriptionNames[type];
                      const isLifetimePlan =
                        (type === SubscriptionType.DashboardStandard && isDashboardLifetime) ||
                        (type === SubscriptionType.SmartMoneyAlerts && isTechnicalAnalysisLifetime);
                      const displayName = isLifetimePlan
                        ? type === SubscriptionType.DashboardStandard
                          ? 'Real-time Options Dashboard Lifetime Plan'
                          : 'Technical Analysis System Lifetime Plan'
                        : name;

                      return (
                        <div
                          className="mb-4 flex items-center justify-between text-sm last:mb-0"
                          key={i}
                        >
                          <div>
                            <div className="font-semibold">{displayName}</div>
                            {!isLifetimePlan && (
                              <div className="mt-1 text-gray-400">
                                Next rebill: {humanReadableEndDate}
                                {!subscription.cancelAtPeriodEnd ? (
                                  <>
                                    <span className="mx-1"> &middot; </span>
                                    <SessionLink
                                      isCreatingSession={isCreatingSession}
                                      className="text-sm text-gray-300 underline"
                                      href="#"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        if (!isCreatingSession) {
                                          if (
                                            USE_PROFITWELL_CANCEL_FLOW ||
                                            window.location.search.includes(NEW_CANCEL_QUERY)
                                          ) {
                                            setSubToCancel(subscription.externalSubscriptionId);
                                          } else {
                                            handleEnterProsperStackCancelFlow(
                                              subscription.externalSubscriptionId,
                                            );
                                          }
                                        }
                                      }}
                                    >
                                      Cancel
                                    </SessionLink>
                                  </>
                                ) : (
                                  <>
                                    <span className="mx-1"> &middot; </span>
                                    <SessionLink
                                      isCreatingSession={isCreatingSession}
                                      className="text-sm text-gray-300 underline"
                                      href="#"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        handleCreatePortalSession();
                                      }}
                                    >
                                      Renew
                                    </SessionLink>
                                  </>
                                )}
                              </div>
                            )}
                          </div>
                          <div>
                            {subscription.cancelAtPeriodEnd && !isLifetimePlan ? (
                              <StatusEnding>ENDING</StatusEnding>
                            ) : (
                              <StatusActive>ACTIVE</StatusActive>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </Card>
                <div className="mt-2 pl-1 text-sm">
                  For questions or updates to your subscription, reach out to team@insiderfinance.io
                  and we would be happy to help you out.
                </div>
              </div>
            )}
            {settings.invoices?.length > 0 && !getIsNativePlatform() && (
              <div className="mt-6">
                <div className="flex justify-between">
                  <div className="styled-font mb-2 text-xl font-semibold">Payments</div>
                </div>
                <Card>
                  <div className="p-2">
                    {settings.invoices.map((payment, i) => {
                      const amountPaid = '$' + payment.amountPaid.toFixed(2);
                      const paymentDate = new Date(payment.paidAt * 1000).toLocaleDateString();
                      const url = payment.url;
                      return (
                        <div className="mb-4 flex justify-between text-sm last:mb-0" key={i}>
                          <div className="flex">
                            <div>{amountPaid}</div>
                            <div className="ml-4 text-gray-500">{paymentDate}</div>
                          </div>
                          <a
                            href={url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="underline"
                          >
                            View Invoice
                          </a>
                        </div>
                      );
                    })}
                  </div>
                </Card>
              </div>
            )}
          </div>
          {!hasSubscriptions && !getIsNativePlatform() && (
            <div className="mt-16">
              <PricingCards />
            </div>
          )}
        </div>
      </PageWrapper>
      <Transition.Root show={isUpgradeOpen} as={React.Fragment}>
        <Dialog
          as="div"
          className="relative z-10"
          onClose={() => {
            setIsUpgradeOpen(false);
          }}
        >
          <Transition.Child
            as={React.Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          {!isCreatingSession && (
            <div className="fixed inset-0 z-10 overflow-y-auto">
              <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                <Transition.Child
                  as={React.Fragment}
                  enter="ease-out duration-300"
                  enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                  enterTo="opacity-100 translate-y-0 sm:scale-100"
                  leave="ease-in duration-200"
                  leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                  leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                >
                  <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-palette-black-1 px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-4xl sm:px-16 sm:pb-20 sm:pt-12">
                    <div className="absolute right-4 top-4">
                      <button onClick={() => setIsUpgradeOpen(false)}>
                        <XIcon className="h-6 w-6" />
                      </button>
                    </div>
                    <div>
                      <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
                        <ArrowUpIcon className="h-6 w-6 text-green-600" aria-hidden="true" />
                      </div>
                      <div className="mt-3 text-center sm:mt-5">
                        <Dialog.Title
                          as="h3"
                          className="styled-font text-4xl font-semibold text-white"
                        >
                          Upgrade and Save
                        </Dialog.Title>
                        {upgradeRequestStatus === RequestStatus.Idle && (
                          <>
                            <div className="mt-3">
                              <p className="mx-auto max-w-sm text-sm text-palette-black-7">
                                Upgrade your subscription to save money and get the most out of
                                InsiderFinance.
                              </p>
                            </div>
                          </>
                        )}
                        {upgradeRequestStatus === RequestStatus.Error && (
                          <div className="mt-3">
                            <p className="text-sm text-red-200">
                              There was an error. Please email team@insiderfinance.io, and we will
                              help you upgrade.
                            </p>
                          </div>
                        )}
                      </div>
                      {upgradeRequestStatus === RequestStatus.InProgress && (
                        <div className="flex items-center justify-center py-12">
                          <PulseLoader />
                        </div>
                      )}
                      {upgradeRequestStatus === RequestStatus.Success && (
                        <div className="flex flex-col items-center justify-center py-12">
                          <h1 className="styled-font text-5xl font-bold text-green-400">
                            Success!
                          </h1>
                          <button
                            onClick={() => setIsUpgradeOpen(false)}
                            className="mt-4 underline"
                          >
                            Close
                          </button>
                        </div>
                      )}
                      {upgradeRequestStatus === RequestStatus.Idle && (
                        <div
                          className={classNames(
                            'flex flex-col justify-center md:flex-row md:space-x-16',
                          )}
                        >
                          {stripeSubscriptionDetails.firstActiveDuration !== 'year' && (
                            <form
                              onSubmit={async () => {
                                try {
                                  setUpgradeRequestStatus(RequestStatus.InProgress);
                                  await api.post('payments/stripe/subscriptions/upgrade-active', {
                                    payload: {
                                      currentPriceId: stripeSubscriptionDetails.firstActivePriceId,
                                      currentSubscriptionId:
                                        stripeSubscriptionDetails.firstActiveSubscriptionId,
                                      requestedDuration: SaleTerm.Annual,
                                      requestedSubscriptionType:
                                        stripeSubscriptionDetails.isFirstActiveTechnicalAnalysis
                                          ? ALERTS_SHORT_NAME
                                          : DASHBOARD_SHORT_NAME,
                                    },
                                  });
                                  setUpgradeRequestStatus(RequestStatus.Success);
                                } catch (_error) {
                                  setUpgradeRequestStatus(RequestStatus.Error);
                                }
                              }}
                              className="w-full text-center md:w-1/2"
                            >
                              <div className="mb-4 mt-12 text-center text-xl font-semibold text-green-400">
                                Annual Subscription
                              </div>
                              <div className="mt-4 flex items-center justify-center text-center text-3xl">
                                <span className="text-2xl text-red-400 line-through">
                                  $
                                  {stripeSubscriptionDetails.isFirstActiveTechnicalAnalysis
                                    ? upgradePrices.alerts.annual
                                    : upgradePrices.dashboard.annual}
                                </span>{' '}
                                <span className="ml-2 font-semibold">
                                  $
                                  {Math.floor(
                                    (stripeSubscriptionDetails.isFirstActiveTechnicalAnalysis
                                      ? upgradePrices.alerts.annual
                                      : upgradePrices.dashboard.annual) *
                                      (1 - upgradePrices.discount),
                                  )}{' '}
                                  first year
                                </span>
                              </div>
                              <div className="mt-1 text-xs text-gray-300">
                                Our most popular subscription
                              </div>
                              <div className="mt-4 text-sm">
                                Get {upgradePrices.discount * 100}% off your first year of
                                InsiderFinance when you upgrade today. That's like receiving 5
                                months for free. Unused time from your current subscription is
                                prorated and deducted from this payment.
                              </div>
                              <InlineCheckboxWrapperLabel>
                                <InlineCheckbox type="checkbox" required name="lifetime-discount" />
                                <CheckboxInlineLabel>
                                  Yes! Lock in my annual discount!
                                </CheckboxInlineLabel>
                              </InlineCheckboxWrapperLabel>
                              <button
                                type="submit"
                                className="mt-4 inline-flex w-full justify-center rounded-md border border-transparent bg-button-color px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-button-color focus:ring-offset-2 sm:col-start-2 sm:text-sm"
                              >
                                Upgrade to Annual Now
                              </button>
                            </form>
                          )}
                          <form
                            onSubmit={async () => {
                              try {
                                setUpgradeRequestStatus(RequestStatus.InProgress);
                                await api.post('payments/stripe/subscriptions/upgrade-active', {
                                  payload: {
                                    currentPriceId: stripeSubscriptionDetails.firstActivePriceId,
                                    currentSubscriptionId:
                                      stripeSubscriptionDetails.firstActiveSubscriptionId,
                                    requestedDuration: SaleTerm.Lifetime,
                                    requestedSubscriptionType:
                                      stripeSubscriptionDetails.isFirstActiveTechnicalAnalysis
                                        ? ALERTS_SHORT_NAME
                                        : DASHBOARD_SHORT_NAME,
                                  },
                                });
                                setUpgradeRequestStatus(RequestStatus.Success);
                              } catch (_error) {
                                setUpgradeRequestStatus(RequestStatus.Error);
                              }
                            }}
                            className="w-full text-center md:w-1/2"
                          >
                            <div className="mb-4 mt-12 text-center text-xl font-semibold text-green-400">
                              Lifetime Subscription
                            </div>
                            <div className="mt-4 text-center text-3xl font-semibold">
                              $
                              {stripeSubscriptionDetails.isFirstActiveTechnicalAnalysis
                                ? upgradePrices.alerts.lifetime
                                : upgradePrices.dashboard.lifetime}{' '}
                              once
                            </div>
                            <div className="mt-1 text-xs text-gray-300">
                              A one-time payment to get access for life
                            </div>
                            <div className="mt-4 text-sm">
                              Get access to InsiderFinance for life and never pay again. You'll
                              receive all future upgrades and new features at no additional cost.
                              Don't miss out on lifetime gains.
                            </div>
                            <InlineCheckboxWrapperLabel>
                              <InlineCheckbox type="checkbox" required name="lifetime-discount" />
                              <CheckboxInlineLabel>
                                Yes! Lock in my lifetime discount!
                              </CheckboxInlineLabel>
                            </InlineCheckboxWrapperLabel>
                            <button
                              type="submit"
                              className="mt-4 inline-flex w-full justify-center rounded-md border border-transparent bg-button-color px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-button-color focus:ring-offset-2 sm:col-start-2 sm:text-sm"
                            >
                              Upgrade to Lifetime Now
                            </button>
                          </form>
                        </div>
                      )}
                    </div>
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </div>
          )}
        </Dialog>
      </Transition.Root>
      <Transition.Root show={isResubscribeOpen} as={React.Fragment}>
        <Dialog
          as="div"
          className="relative z-10"
          onClose={() => {
            setIsResubscribeOpen(false);
          }}
        >
          <Transition.Child
            as={React.Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          {!isCreatingSession && (
            <div className="fixed inset-0 z-10 overflow-y-auto">
              <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                <Transition.Child
                  as={React.Fragment}
                  enter="ease-out duration-300"
                  enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                  enterTo="opacity-100 translate-y-0 sm:scale-100"
                  leave="ease-in duration-200"
                  leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                  leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                >
                  <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-palette-black-1 px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-3xl sm:px-8 sm:pb-12 sm:pt-8">
                    <div className="absolute right-4 top-4">
                      <button onClick={() => setIsResubscribeOpen(false)}>
                        <XIcon className="h-6 w-6" />
                      </button>
                    </div>
                    <div>
                      <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
                        <CheckIcon className="h-6 w-6 text-green-600" aria-hidden="true" />
                      </div>
                      <div className="mt-3 text-center sm:mt-5">
                        <Dialog.Title
                          as="h3"
                          className="styled-font text-2xl font-semibold leading-6 text-white"
                        >
                          Select a Plan to Resubscribe with 30% Off FOREVER
                        </Dialog.Title>
                        {resubscribeRequestStatus === RequestStatus.Idle && (
                          <>
                            <div className="mt-3">
                              <p className="mx-auto max-w-lg text-sm text-palette-black-7">
                                Resubscribe now and receive a 30% lifetime discount on any plan.
                                That's the biggest discount we offer, and your discount never
                                expires!
                              </p>
                            </div>
                          </>
                        )}
                        {resubscribeRequestStatus === RequestStatus.Error && (
                          <div className="mt-3">
                            <p className="text-sm text-red-200">
                              There was an error. Please email team@insiderfinance.io, and we will
                              help you resubscribe.
                            </p>
                          </div>
                        )}
                      </div>
                      {resubscribeRequestStatus === RequestStatus.InProgress && (
                        <div className="flex items-center justify-center py-12">
                          <PulseLoader />
                        </div>
                      )}
                      {resubscribeRequestStatus === RequestStatus.Success && (
                        <div className="flex flex-col items-center justify-center py-12">
                          <h1 className="styled-font text-5xl font-bold text-green-400">
                            Success!
                          </h1>
                          <button
                            onClick={() => setIsResubscribeOpen(false)}
                            className="mt-4 underline"
                          >
                            Close
                          </button>
                        </div>
                      )}
                      {resubscribeRequestStatus === RequestStatus.Idle && (
                        <>
                          <div>
                            <div className="mb-4 mt-12 text-center text-xl font-semibold text-green-400">
                              Dashboard + Technical Analysis
                            </div>
                            <div className="grid grid-cols-1 gap-4 md:grid-cols-3 md:gap-10">
                              <ResubscribeCard
                                term="Monthly"
                                price={resubscribePrices.dashboard.monthly}
                                discount={resubscribePrices.discount}
                                duration={productTerms.monthly}
                                subscriptionType={DASHBOARD_SHORT_NAME}
                                setResubscribeRequestStatus={setResubscribeRequestStatus}
                              />
                              <ResubscribeCard
                                term="Quarterly"
                                price={resubscribePrices.dashboard.quarterly}
                                discount={resubscribePrices.discount}
                                duration={productTerms.quarterly}
                                subscriptionType={DASHBOARD_SHORT_NAME}
                                setResubscribeRequestStatus={setResubscribeRequestStatus}
                              />
                              <ResubscribeCard
                                term="Annual"
                                price={resubscribePrices.dashboard.annual}
                                discount={resubscribePrices.discount}
                                duration={productTerms.annual}
                                subscriptionType={DASHBOARD_SHORT_NAME}
                                setResubscribeRequestStatus={setResubscribeRequestStatus}
                              />
                            </div>
                          </div>
                          <div>
                            <div className="mb-4 mt-12 text-center text-xl font-semibold text-green-400">
                              Technical Analysis
                            </div>
                            <div className="grid grid-cols-1 gap-4 md:grid-cols-3 md:gap-10">
                              <ResubscribeCard
                                term="Monthly"
                                price={resubscribePrices.alerts.monthly}
                                discount={resubscribePrices.discount}
                                duration={productTerms.monthly}
                                subscriptionType={ALERTS_SHORT_NAME}
                                setResubscribeRequestStatus={setResubscribeRequestStatus}
                              />
                              <ResubscribeCard
                                term="Quarterly"
                                price={resubscribePrices.alerts.quarterly}
                                discount={resubscribePrices.discount}
                                duration={productTerms.quarterly}
                                subscriptionType={ALERTS_SHORT_NAME}
                                setResubscribeRequestStatus={setResubscribeRequestStatus}
                              />
                              <ResubscribeCard
                                term="Annual"
                                price={resubscribePrices.alerts.annual}
                                discount={resubscribePrices.discount}
                                duration={productTerms.quarterly}
                                subscriptionType={ALERTS_SHORT_NAME}
                                setResubscribeRequestStatus={setResubscribeRequestStatus}
                              />
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </div>
          )}
        </Dialog>
      </Transition.Root>
      <Transition.Root show={!!subToCancel} as={React.Fragment}>
        <Dialog
          as="div"
          className="relative z-10"
          onClose={() => {
            setSubToCancel('');
            setHasCancelError(false);
            setIsCreatingSession(false);
          }}
        >
          <Transition.Child
            as={React.Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          {!isCreatingSession && (
            <div className="fixed inset-0 z-10 overflow-y-auto">
              <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                <Transition.Child
                  as={React.Fragment}
                  enter="ease-out duration-300"
                  enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                  enterTo="opacity-100 translate-y-0 sm:scale-100"
                  leave="ease-in duration-200"
                  leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                  leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                >
                  <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-palette-black-1 px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                    <div>
                      <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
                        <CheckIcon className="h-6 w-6 text-green-600" aria-hidden="true" />
                      </div>
                      <div className="mt-3 text-center sm:mt-5">
                        <Dialog.Title
                          as="h3"
                          className="styled-font text-lg font-medium leading-6 text-white"
                        >
                          Wait! Have you taken our FREE Masterclass?
                        </Dialog.Title>
                        {!hasCancelError ? (
                          <>
                            <div className="mt-3">
                              <p className="text-sm text-palette-black-7">
                                Our bite-sized lessons and step-by-step walkthroughs make increasing
                                your win rate fast and easy.
                              </p>
                            </div>
                            <div className="mt-3">
                              <p className="text-sm text-palette-black-7">
                                We're incredibly confident anyone can use the InsiderFinance tools
                                to dramatically increase trading returns, whether the market is
                                going up or down. Our data provides trade ideas for high conviction
                                movements that profit for long and short trades.
                              </p>
                            </div>
                          </>
                        ) : (
                          <div className="mt-3">
                            <p className="text-sm text-palette-black-7">
                              There was an error canceling. Please email team@insiderfinance.io.
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="mt-5 sm:mt-6 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3">
                      <Link
                        className="inline-flex w-full justify-center rounded-md border border-transparent bg-button-color px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-button-color focus:ring-offset-2 sm:col-start-2 sm:text-sm"
                        href={COURSES_INDEX_PAGE}
                      >
                        View Masterclass
                      </Link>
                      <button
                        type="button"
                        className="mt-3 inline-flex w-full justify-center rounded-md border border-red-600 bg-transparent px-4 py-2 text-base font-medium text-palette-black-9 shadow-sm hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-button-color focus:ring-offset-2 sm:col-start-1 sm:mt-0 sm:text-sm"
                        onClick={async () => {
                          // @ts-ignore
                          if (typeof window.profitwell !== 'undefined') {
                            setIsCreatingSession(true);
                            try {
                              // @ts-ignore
                              const result = await profitwell('init_cancellation_flow', {
                                subscription_id: subToCancel,
                              });
                              if (result.status === 'retained' || result.status === 'aborted') {
                                // NOTE: Change this for Capacitor
                                window.location.href = '/';
                              } else {
                                await handleDeleteSubscription(subToCancel, result);
                                window.location.reload();
                              }
                            } catch (error) {
                              Sentry.captureException(error);
                              setHasCancelError(true);
                              setIsCreatingSession(false);
                            }
                          } else {
                            Sentry.captureException(new Error('Profitwell not defined to cancel'));
                            setHasCancelError(true);
                            setIsCreatingSession(false);
                          }
                        }}
                      >
                        Cancel Subscription
                      </button>
                    </div>
                    <div>
                      <button
                        className="mb-2 mt-5 w-full text-center text-xs font-bold capitalize text-palette-black-7"
                        onClick={() => {
                          setSubToCancel('');
                          setHasCancelError(false);
                          setIsCreatingSession(false);
                        }}
                      >
                        CLOSE
                      </button>
                    </div>
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </div>
          )}
        </Dialog>
      </Transition.Root>
    </>
  );
}
